import React from 'react';

import {
  FileDownload as FileDownloadIcon,
  FilterAlt as FilterIcon,
  FolderCopy as FolderIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import {
  Box,
  Checkbox,
  CircularProgress,
  IconButton,
  Table as MuiTable,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import {
  type ColumnDef,
  flexRender,
  type OnChangeFn,
  type RowSelectionState,
  type SortingState,
  useTable,
} from '@tanstack/react-table';
import { isFunction, isNotNil, uniqBy } from 'es-toolkit';
import { isArray, isEmpty } from 'es-toolkit/compat';

import { DEFAULT_ROWS_PER_PAGE_OPTIONS } from 'src/providers/PaginationProvider';
import type { SxProp } from 'src/types/mui';
import { composeSx } from 'src/utils/styles';

import { type DefaultDataRow, type TableColumn, TableThemes, type TableTopAction } from '../types';
import { getCellInfo } from '../utils';

import {
  TableBodyRow,
  TableFiltersPopover,
  TableFooter,
  TableRowActionsMenu,
  TableTopMenu,
} from './components';
import { ACTIONS_COLUMN_ID, SELECTION_COLUMN_ID } from './constants';
import { features, type TableFeatureSet } from './features';
import { columnKindStyles, getColumnKind, TableStyles } from './theme';
import type { TableProps, TableSelectedRowId } from './types';
import { handleDownload, handleExport } from './utils';
import { convertGridSortOrder } from './utils/misc';

// stable default: a fresh [] per render would make the derived rowSelection object a new
// identity every render, and useTable's render-time state sync would loop forever
const EMPTY_SELECTED_IDS: TableSelectedRowId[] = [];

export function Table<TRecord extends DefaultDataRow = DefaultDataRow>({
  dataSource,
  rowCount,
  columns,
  rowActions,
  topActions = [],
  bottomActions,
  tableTitle,
  pagination,
  paginationHasNextPage,
  onSearch,
  onSearchCategorySelect,
  searchStyles = {},
  searchLabel,
  searchInputSize,
  searchCategories,
  onSort,
  isRowSelectable,
  excelColumns,
  filesDownloadProps,
  showDownloadIcon = true,
  hideTopActions = false,
  hidePagination = false,
  emptyMessage = 'No Data',
  loading = false,
  disabled = false,
  selectable = false,
  hideTableTopMenu = false,
  onRowClick,
  keyFilter,
  filters,
  filtersGroups,
  selectedIds = EMPTY_SELECTED_IDS,
  setSelectedIds,
  getRowId,
  hideFooter = false,
  getRowClassName,
  extraTopNode,
  isTrackFilter = false,
  tableTheme = TableThemes.default,
  isRightMenu = false,
  onRefresh,
}: TableProps<TRecord>) {
  const [sorting, setSorting] = React.useState<SortingState>([]);

  // latest-ref pattern: cell closures read these at render time without putting the
  // per-fetch identities into the columnDefs memo (which would rebuild all table objects)
  const dataSourceRef = React.useRef(dataSource);
  dataSourceRef.current = dataSource;
  const rowActionsRef = React.useRef(rowActions);
  rowActionsRef.current = rowActions;

  // selection persists across server pages while dataSource holds only the current page —
  // cache every selected row we've seen so export/download include off-page selections
  const selectedRowsCacheRef = React.useRef(new Map<TableSelectedRowId, TRecord>());
  const selectedDataRows = React.useMemo((): TRecord[] => {
    const cache = selectedRowsCacheRef.current;
    const ids = new Set(selectedIds);

    for (const id of cache.keys()) {
      if (!ids.has(id)) {
        cache.delete(id);
      }
    }
    for (const dataRow of dataSource) {
      if (isNotNil(dataRow.id) && ids.has(dataRow.id)) {
        cache.set(dataRow.id, dataRow);
      }
    }

    return selectedIds.map(id => cache.get(id)).filter(isNotNil);
  }, [dataSource, selectedIds]);

  const hasSearch = isNotNil(onSearch) && isFunction(onSearch);
  const hasSorting = isNotNil(onSort) && isFunction(onSort);
  const hasFilters = isNotNil(filters) && filters.length > 0;
  const hasPagination = !!pagination && !hidePagination;
  const hasRowActions = isArray(rowActions) && !isEmpty(rowActions);

  const rowSelection = React.useMemo((): RowSelectionState => {
    return Object.fromEntries(selectedIds.map(id => [String(id), true]));
  }, [selectedIds]);

  const handleSortingChange: OnChangeFn<SortingState> = updater => {
    const nextSorting = typeof updater === 'function' ? updater(sorting) : updater;
    setSorting(nextSorting);

    if (onSort) {
      const option = nextSorting[0];
      const columnByKey = option ? columns.find(column => column.key === option.id) : undefined;
      onSort(
        option
          ? {
              columnKey: option.id,
              order: convertGridSortOrder(option.desc ? 'desc' : 'asc'),
              nonNull: columnByKey?.nonNullSortable,
            }
          : undefined,
      );
    }

    // changing the sort re-orders the whole set — a middle page is meaningless
    if (!loading && hasPagination && pagination) {
      pagination.onModelChange?.(0, pagination.rowsPerPage);
    }
  };

  const handleRowSelectionChange: OnChangeFn<RowSelectionState> = updater => {
    if (!setSelectedIds) {
      console.error('Table: "selectable" needs "setSelectedIds" — selection is a controlled prop');
      return;
    }

    const nextRowSelection = typeof updater === 'function' ? updater(rowSelection) : updater;
    setSelectedIds(Object.keys(nextRowSelection));
  };

  const renderColumn = React.useCallback(
    (column: TableColumn<TRecord>): ColumnDef<TableFeatureSet, TRecord> => {
      const { dataPath, render: renderFn } = column;

      const hasRenderFn = isFunction(renderFn);
      const hasDataPath = isNotNil(dataPath) && dataPath !== '';

      return {
        id: column.key,
        // accessorFn is required by getCanSort; sorting itself is server-side
        accessorFn: dataRow => (hasDataPath ? getCellInfo(dataRow, dataPath) : dataRow),
        header: () => column.title,
        enableSorting: (column.sortable || false) && hasSorting,
        cell: ({ row }) => {
          const dataRow = row.original;
          const valueByDataPath = hasDataPath ? getCellInfo(dataRow, dataPath) : dataRow;

          if (hasRenderFn) {
            return (
              <Box width="100%">
                {renderFn(valueByDataPath, dataRow, {
                  dataSource: dataSourceRef.current,
                  rowActions: rowActionsRef.current,
                })}
              </Box>
            );
          }
          if (hasDataPath) {
            return String(valueByDataPath ?? '');
          }

          console.error(`Column by "${dataRow.id}" id expected render or dataPath properties`);
          return null;
        },
      };
    },
    [hasSorting],
  );

  const selectionColumn = React.useMemo((): ColumnDef<TableFeatureSet, TRecord> | undefined => {
    if (!selectable) return undefined;

    return {
      id: SELECTION_COLUMN_ID,
      // page-scoped APIs: selection persists across server pages, so the whole-map variants
      // would report checked/indeterminate for rows that are not on screen
      header: ({ table }) => (
        <Checkbox
          checked={table.getIsAllPageRowsSelected()}
          indeterminate={!table.getIsAllPageRowsSelected() && table.getIsSomePageRowsSelected()}
          onChange={table.getToggleAllPageRowsSelectedHandler()}
          inputProps={{ 'aria-label': 'Select all rows' }}
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          disabled={!row.getCanSelect()}
          onChange={row.getToggleSelectedHandler()}
          onClick={stopPropagation}
          inputProps={{ 'aria-label': 'Select row' }}
        />
      ),
    };
  }, [selectable]);

  const actionsColumn = React.useMemo((): ColumnDef<TableFeatureSet, TRecord> | undefined => {
    if (!hasRowActions) return undefined;

    return {
      id: ACTIONS_COLUMN_ID,
      header: 'Actions',
      enableSorting: false,
      cell: ({ row }) => (
        <TableRowActionsMenu dataRow={row.original} rowActions={rowActionsRef.current ?? []} />
      ),
    };
  }, [hasRowActions]);

  const columnDefs = React.useMemo((): ColumnDef<TableFeatureSet, TRecord>[] => {
    if (columns.some(column => column.key === ACTIONS_COLUMN_ID)) {
      console.error(`You must not pass columns with "${ACTIONS_COLUMN_ID}" key to the table`);
      return [];
    }

    if (columns.length !== uniqBy(columns, column => column.key).length) {
      console.error('Not all table columns have unique keys');
      return [];
    }

    return [
      selectionColumn,
      ...columns.filter(column => !column.hidden).map(renderColumn),
      actionsColumn,
    ].filter(isNotNil);
  }, [columns, selectionColumn, actionsColumn, renderColumn]);

  const columnsByKey = React.useMemo(() => {
    return new Map(columns.map(column => [column.key, column]));
  }, [columns]);

  // under fixed layout column widths come from the header row only, so they stay stable when
  // data changes (sort / page-size); this min keeps the old grid's horizontal-scroll behavior
  // when the summed widths exceed the viewport (selection 48 + data cols 150 each + actions 100)
  const tableMinWidth = React.useMemo(
    () =>
      columnDefs.reduce((total, columnDef) => {
        if (columnDef.id === SELECTION_COLUMN_ID) return total + 48;
        if (columnDef.id === ACTIONS_COLUMN_ID) return total + 100;
        return total + (columnsByKey.get(columnDef.id ?? '')?.width ?? 150);
      }, 0),
    [columnDefs, columnsByKey],
  );

  const getTableRowId = React.useCallback(
    (originalRow: TRecord, index: number): string => {
      const id = getRowId?.(originalRow) ?? originalRow.id;
      if (isNotNil(id)) {
        return String(id);
      }

      console.error(
        'Table row has no "id" — pass getRowId or add an "id" field; falling back to row index',
      );
      return String(index);
    },
    [getRowId],
  );

  const table = useTable<TableFeatureSet, TRecord>({
    features,
    data: dataSource,
    columns: columnDefs,
    getRowId: getTableRowId,
    state: { sorting, rowSelection },
    onSortingChange: handleSortingChange,
    onRowSelectionChange: handleRowSelectionChange,
    manualSorting: true,
    enableMultiSort: false,
    // tanstack's auto heuristic starts non-string columns desc; keep DataGrid's asc-first cycle
    sortDescFirst: false,
    enableRowSelection: selectable
      ? isRowSelectable
        ? row => isRowSelectable({ id: row.id, row: row.original })
        : true
      : false,
  });

  const filterAction = React.useMemo(
    () => ({
      actionName: 'Filter',
      icon: <FilterIcon />,
      show: hasFilters,
      render() {
        return (
          <TableFiltersPopover
            key={this.actionName}
            keyFilter={keyFilter}
            filters={filters || []}
            filtersGroups={filtersGroups || []}
            disabled={loading}
            actionIcon={this.icon}
            isTrackFilter={isTrackFilter}
            pagination={pagination}
          />
        );
      },
    }),
    [hasFilters, keyFilter, filters, filtersGroups, loading, isTrackFilter, pagination],
  );

  const defaultTopActions = React.useMemo((): TableTopAction[] => {
    return [
      {
        actionName: 'Refresh data',
        show: !!onRefresh,
        icon: <RefreshIcon />,
        onAction: () => {
          onRefresh?.();
        },
        render() {
          return (
            <IconButton key={this.actionName} onClick={this.onAction} disabled={loading}>
              {this.icon}
            </IconButton>
          );
        },
      },
      {
        actionName: 'Export data',
        show: !!excelColumns && showDownloadIcon,
        icon: <FileDownloadIcon />,
        onAction: () => handleExport<TRecord>(excelColumns, selectedDataRows),
        render() {
          return (
            <IconButton
              key={this.actionName}
              onClick={this.onAction}
              disabled={!selectedDataRows.length || loading}
            >
              {this.icon}
            </IconButton>
          );
        },
      },
      {
        actionName: 'Download files',
        icon: <FolderIcon />,
        show: !!filesDownloadProps,
        onAction: () =>
          handleDownload<TRecord>(
            selectedDataRows,
            filesDownloadProps?.filePath,
            filesDownloadProps?.companyPath,
          ),
        render() {
          return (
            <IconButton
              key={this.actionName}
              onClick={this.onAction}
              disabled={!selectedDataRows.length}
            >
              {this.icon}
            </IconButton>
          );
        },
      },
    ];
  }, [excelColumns, filesDownloadProps, loading, onRefresh, selectedDataRows, showDownloadIcon]);

  const definedTopActions = React.useMemo((): TableTopAction[] => {
    return [...topActions, ...defaultTopActions].filter(action => action.show ?? true);
  }, [defaultTopActions, topActions]);

  const tableTopMenuProps = React.useMemo((): React.ComponentProps<typeof TableTopMenu> => {
    return {
      tableTitle,
      tableTheme,
      searchProps: {
        hasSearch,
        onSearch,
        onSearchCategorySelect,
        searchStyles,
        searchLabel,
        searchInputSize,
        searchCategories,
      },
      actionProps: {
        actions: definedTopActions,
        hidden: hideTopActions,
        filterAction: filterAction.show ? filterAction : undefined,
      },
      hidden: hideTableTopMenu,
      extraTopNode,
      isRightMenu,
    };
  }, [
    tableTitle,
    tableTheme,
    hasSearch,
    onSearch,
    onSearchCategorySelect,
    searchStyles,
    searchLabel,
    searchInputSize,
    searchCategories,
    definedTopActions,
    hideTopActions,
    filterAction,
    hideTableTopMenu,
    extraTopNode,
    isRightMenu,
  ]);

  const tableFooterProps = React.useMemo((): React.ComponentProps<typeof TableFooter> => {
    return {
      tableBottomActionsProps: {
        bottomActions,
        loading,
        disabled,
      },
      selectedCount: selectedIds.length,
      totalRowCount: rowCount ?? dataSource.length,
      paginationProps:
        hasPagination && pagination
          ? {
              count: rowCount ?? -1,
              page: pagination.page,
              rowsPerPage: pagination.rowsPerPage,
              rowsPerPageOptions: pagination.rowsPerPageOptions || DEFAULT_ROWS_PER_PAGE_OPTIONS,
              hasNextPage: paginationHasNextPage,
              onPageChange: page => {
                if (!loading) {
                  pagination.onModelChange?.(page, pagination.rowsPerPage);
                }
              },
              onRowsPerPageChange: rowsPerPage => {
                if (!loading) {
                  pagination.onModelChange?.(0, rowsPerPage);
                }
              },
            }
          : undefined,
    };
  }, [
    bottomActions,
    disabled,
    loading,
    hasPagination,
    pagination,
    rowCount,
    paginationHasNextPage,
    selectedIds,
    dataSource,
  ]);

  const rows = table.getRowModel().rows;

  return (
    <Box sx={rootStyles(tableTheme, hideFooter)}>
      <TableTopMenu {...tableTopMenuProps} />
      <TableContainer sx={tableContainerStyles(tableTheme)} aria-busy={loading || undefined}>
        {loading ? (
          <Box sx={loadingOverlayStyles}>
            <CircularProgress />
          </Box>
        ) : null}
        <MuiTable sx={{ tableLayout: 'fixed', minWidth: tableMinWidth }}>
          {tableTitle ? (
            <Box component="caption" sx={visuallyHidden}>
              {tableTitle}
            </Box>
          ) : null}
          <TableHead>
            {table.getHeaderGroups().map(headerGroup => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map(header => {
                  const sortDirection = header.column.getIsSorted();
                  const columnKind = getColumnKind(header.column.id);

                  return (
                    <TableCell
                      key={header.id}
                      component="th"
                      scope="col"
                      sortDirection={sortDirection}
                      padding={columnKind === 'selection' ? 'checkbox' : 'normal'}
                      align={columnsByKey.get(header.column.id)?.align}
                      sx={composeSx(
                        headCellStyles(tableTheme),
                        columnKindStyles[columnKind],
                        columnKind === 'actions' ? headActionsCellStyles : undefined,
                        columnKind === 'data'
                          ? { width: columnsByKey.get(header.column.id)?.width }
                          : undefined,
                      )}
                    >
                      {header.column.getCanSort() ? (
                        <TableSortLabel
                          active={sortDirection !== false}
                          direction={sortDirection === false ? 'asc' : sortDirection}
                          onClick={loading ? undefined : header.column.getToggleSortingHandler()}
                        >
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {sortDirection !== false && (
                            <Box component="span" sx={visuallyHidden}>
                              {sortDirection === 'desc' ? 'sorted descending' : 'sorted ascending'}
                            </Box>
                          )}
                        </TableSortLabel>
                      ) : (
                        flexRender(header.column.columnDef.header, header.getContext())
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableHead>
          <TableBody>
            {rows.map(row => (
              <TableBodyRow
                key={row.id}
                row={row}
                selected={row.getIsSelected()}
                className={getRowClassName?.({ id: row.id, row: row.original })}
                tableTheme={tableTheme}
                columnsByKey={columnsByKey}
                onRowClick={onRowClick}
              />
            ))}
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={columnDefs.length || 1} align="center" sx={emptyCellStyles}>
                  {!loading && (
                    <Typography variant="body2" textTransform="uppercase">
                      {emptyMessage}
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </MuiTable>
      </TableContainer>
      {!hideFooter && (
        <Box sx={footerStyles(tableTheme)}>
          <TableFooter {...tableFooterProps} />
        </Box>
      )}
    </Box>
  );
}

// the checkbox toggles selection itself; the click must not bubble into onRowClick
const stopPropagation = (event: React.SyntheticEvent) => event.stopPropagation();

const rootStyles =
  (tableTheme: TableThemes, isFooterHidden?: boolean): SxProp =>
  theme => ({
    display: 'flex',
    flexDirection: 'column',
    fontSize: theme.typography.body2.fontSize,
    padding: TableStyles[tableTheme].padding,
    ...(isFooterHidden
      ? {
          borderBottom: TableStyles[tableTheme].border,
        }
      : null),
  });

const tableContainerStyles = (tableTheme: TableThemes): SxProp => ({
  position: 'relative',
  borderRadius: '8px 8px 0 0',
  border: TableStyles[tableTheme].border,
  borderBottom: 0,
});

// the thead row has no background of its own, so 'inherit' would leave the sticky header
// cell transparent; pin it opaque
const headActionsCellStyles: SxProp = theme => ({
  backgroundColor: theme.palette.common.white,
});

const headCellStyles =
  (tableTheme: TableThemes): SxProp =>
  theme => ({
    backgroundColor: theme.palette.common.white,
    padding: TableStyles[tableTheme].tableHeadCellPadding,
    height: 56,
    borderColor: theme.palette.divider,
    whiteSpace: 'nowrap',
  });

const loadingOverlayStyles: SxProp = {
  position: 'absolute',
  inset: 0,
  zIndex: 3,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: 'rgba(255, 255, 255, 0.6)',
};

const emptyCellStyles: SxProp = {
  height: 144,
  border: 0,
};

const footerStyles = (tableTheme: TableThemes): SxProp => ({
  borderRadius: '0 0 8px 8px',
  border: TableStyles[tableTheme].border ? TableStyles[tableTheme].border : undefined,
});
